import { IKeyValue } from "motw_core/Entity/IKeyValue";
import { ESharedFunction } from "motw_core/Enums/ESharedFunction";
import * as React from "react";
import { GetDefaultKeyValue } from "source/app/infrastructure/Data/KeyValues/defaultKeyValue";
import { UpdateKeyValueAsync } from "source/app/infrastructure/Data/KeyValues/UpdateKeyValue";
import {
    GetBookOfMindVoiceDescription,
    GetGoogleBookOfMindVoiceDescription,
    GetSharedOrPersistedBookOfMindVoiceKeyValue,
    GetSharedOrPersistedBookOfMindVoicePreviewKeyValue,
    GetGoogleBookOfMindVoiceName,
    GetGoogleBookOfMindVoiceSelection,
    GetSortedBookOfMindVoices,
    googleBookOfMindAlternatingVoiceName,
    googleBookOfMindTunedVoiceNames,
    googleBookOfMindVoiceOptions,
    IsGoogleBookOfMindAlternatingVoice,
    IsGoogleBookOfMindVoice,
    ResolveBookOfMindVoice,
    ToggleBookOfMindVoicePreviewAsync,
    WaitForBookOfMindVoicesAsync,
} from "source/app/presentation/MainPanel/Workflows/BookOfMind/Speech/Helpers/BookOfMindVoice";
import { bookOfMindSpeech } from "source/app/presentation/MainPanel/Workflows/BookOfMind/Speech/BookOfMindSpeech";
import Glyphicon from "source/app/presentation/Shared/Glyphicon/Glyphicon";
import { EGlyphicon } from "source/app/presentation/Shared/Glyphicon/EGlyphicon";
import Hyperlink from "source/app/presentation/Shared/Link/Hyperlink";
import { StyleClassAuto } from "source/app/presentation/Shared/Styles/Auto/StyleClassAuto";
import { TouchActionButton } from "source/app/presentation/Shared/TouchActionButton/TouchActionButton";
import { ShareState } from "source/app/services/UI/State/ShareState";

const voicePreviewText = "This is a Book of Mind voice preview.";

export function BookOfMindVoicePicker() {
    const initialVoiceKeyValue = {
        ...(GetSharedOrPersistedBookOfMindVoiceKeyValue() ??
            GetDefaultKeyValue()),
    };
    const initialPreviewKeyValue =
        GetSharedOrPersistedBookOfMindVoicePreviewKeyValue();

    const [voicePreference, setVoicePreference] =
        React.useState<IKeyValue>(initialVoiceKeyValue);
    const [voicePreviewPreference, setVoicePreviewPreference] =
        React.useState<IKeyValue>(initialPreviewKeyValue);
    const [voices, setVoices] = React.useState<SpeechSynthesisVoice[]>([]);

    ShareState(
        ESharedFunction.BookOfMind_ttsVoice,
        voicePreference,
        setVoicePreference
    );
    ShareState(
        ESharedFunction.BookOfMind_ttsPreviewEnabled,
        voicePreviewPreference,
        setVoicePreviewPreference
    );

    React.useEffect(() => {
        let isMounted = true;
        const synth = window.speechSynthesis;

        WaitForBookOfMindVoicesAsync(synth).then((loadedVoices) => {
            if (isMounted) {
                setVoices(GetSortedBookOfMindVoices(loadedVoices));
            }
        });

        return () => {
            isMounted = false;
        };
    }, []);

    const savedVoiceName = GetUsableSavedVoiceName(voicePreference.value);
    const isGoogleVoiceSelected = IsGoogleBookOfMindVoice(savedVoiceName);
    const isAlternatingVoiceSelected = IsGoogleBookOfMindAlternatingVoice(
        savedVoiceName
    );
    const selectedVoice = ResolveBookOfMindVoice(voices, savedVoiceName);
    const selectedVoiceName = isAlternatingVoiceSelected
        ? googleBookOfMindAlternatingVoiceName
        : isGoogleVoiceSelected
        ? GetGoogleBookOfMindVoiceSelection(
              GetGoogleBookOfMindVoiceName(savedVoiceName)
          )
        : selectedVoice?.name ?? "";
    const activeVoiceLabel = isAlternatingVoiceSelected
        ? GetGoogleBookOfMindVoiceDescription(
              googleBookOfMindAlternatingVoiceName
          )
        : isGoogleVoiceSelected
        ? GetGoogleBookOfMindVoiceDescription(
              GetGoogleBookOfMindVoiceName(savedVoiceName)
          )
        : selectedVoice
        ? GetBookOfMindVoiceDescription(selectedVoice)
        : "Loading voices...";
    const previewEnabled = voicePreviewPreference.value !== "false";
    const savedVoiceIsUnavailable =
        !isGoogleVoiceSelected &&
        savedVoiceName.length > 0 &&
        selectedVoice !== undefined &&
        selectedVoice.name !== savedVoiceName;
    const voiceSelections = [
        ...googleBookOfMindVoiceOptions.map(GetGoogleBookOfMindVoiceSelection),
        googleBookOfMindAlternatingVoiceName,
        ...voices.map((voice) => voice.name),
    ];

    const SelectVoice = (selectedValue: string) => {
        bookOfMindSpeech.InvalidateGoogleSpeechPrefetch();
        bookOfMindSpeech.StopPageAudio("voice-changed");

        const currentVoices =
            window.speechSynthesis.getVoices().length > 0
                ? GetSortedBookOfMindVoices(window.speechSynthesis.getVoices())
                : voices;
        const selectedVoice = ResolveBookOfMindVoice(
            currentVoices,
            selectedValue
        );

        if (
            previewEnabled &&
            IsGoogleBookOfMindAlternatingVoice(selectedValue)
        ) {
            void PreviewAlternatingGoogleVoicesAsync().catch((error: unknown) =>
                console.error("Book of Mind voice preview failed", error)
            );
        } else if (previewEnabled && IsGoogleBookOfMindVoice(selectedValue)) {
            void bookOfMindSpeech
                .SpeakPreviewTextAsync(voicePreviewText, selectedValue)
                .catch((error: unknown) =>
                    console.error("Book of Mind voice preview failed", error)
                );
        } else if (previewEnabled && selectedVoice) {
            void bookOfMindSpeech
                .SpeakPreviewTextWithVoice(voicePreviewText, selectedVoice)
                .catch((error: unknown) =>
                    console.error("Book of Mind voice preview failed", error)
                );
        } else if (previewEnabled) {
            void bookOfMindSpeech
                .SpeakPreviewTextAsync(voicePreviewText, selectedValue)
                .catch((error: unknown) =>
                    console.error("Book of Mind voice preview failed", error)
                );
        }

        void UpdateKeyValueAsync(
            ESharedFunction.BookOfMind_ttsVoice,
            selectedValue
        );
    };

    const SelectAdjacentVoice = (offset: number) => {
        if (voiceSelections.length === 0) {
            return;
        }

        const currentIndex = voiceSelections.indexOf(selectedVoiceName);
        const startIndex = currentIndex >= 0 ? currentIndex : 0;
        const nextIndex =
            (startIndex + offset + voiceSelections.length) %
            voiceSelections.length;

        SelectVoice(voiceSelections[nextIndex]);
    };

    return (
        <div className={containerStyleClass}>
            <div className={titleStyleClass}>Book of Mind voice</div>
            <div className={activeVoiceStyleClass}>{`Using: ${activeVoiceLabel}`}</div>
            {savedVoiceIsUnavailable ? (
                <div className={warningStyleClass}>
                    Saved voice unavailable on this device.
                </div>
            ) : null}
            <div className={controlRowStyleClass}>
                <TouchActionButton
                    ariaLabel="Previous Book of Mind voice"
                    className={voiceArrowButtonStyleClass}
                    onActivate={() => SelectAdjacentVoice(-1)}
                >
                    ‹
                </TouchActionButton>
                <select
                    value={selectedVoiceName}
                    onChange={(event) => SelectVoice(event.target.value)}
                    className={selectStyleClass}
                >
                    {googleBookOfMindVoiceOptions.map((voiceName) => (
                        <option
                            key={voiceName}
                            value={GetGoogleBookOfMindVoiceSelection(voiceName)}
                        >
                            {GetGoogleBookOfMindVoiceDescription(voiceName)}
                        </option>
                    ))}
                    <option
                        value={googleBookOfMindAlternatingVoiceName}
                    >
                        {GetGoogleBookOfMindVoiceDescription(
                            googleBookOfMindAlternatingVoiceName
                        )}
                    </option>
                    {voices.map((voice) => (
                        <option key={voice.name} value={voice.name}>
                            {GetBookOfMindVoiceDescription(voice)}
                        </option>
                    ))}
                </select>
                <TouchActionButton
                    ariaLabel="Next Book of Mind voice"
                    className={voiceArrowButtonStyleClass}
                    onActivate={() => SelectAdjacentVoice(1)}
                >
                    ›
                </TouchActionButton>
                <Hyperlink
                    whenClicked={async () => {
                        await ToggleBookOfMindVoicePreviewAsync();
                    }}
                    classes={speakerButtonStyleClass}
                    tooltip={previewEnabled ? "Voice preview on" : "Voice preview muted"}
                >
                    <Glyphicon
                        icon={previewEnabled ? EGlyphicon["volume-up"] : EGlyphicon["volume-off"]}
                    />
                </Hyperlink>
            </div>
        </div>
    );
}

async function PreviewAlternatingGoogleVoicesAsync() {
    for (const voiceName of googleBookOfMindTunedVoiceNames) {
        await bookOfMindSpeech.SpeakPreviewTextAsync(
            voicePreviewText,
            GetGoogleBookOfMindVoiceSelection(voiceName)
        );
    }
}

function GetUsableSavedVoiceName(value: string | undefined) {
    if (!value) {
        return googleBookOfMindAlternatingVoiceName;
    }

    const trimmedValue = value.trim();

    if (
        !trimmedValue ||
        trimmedValue.startsWith("[") ||
        trimmedValue.startsWith("{")
    ) {
        return googleBookOfMindAlternatingVoiceName;
    }

    return trimmedValue;
}

const containerStyleClass = StyleClassAuto({
    color: "rgb(208, 208, 208)",
    display: "block",
    margin: "4px 0 7px",
    padding: "7px 8px",
    whiteSpace: "normal",
    background: "rgba(0, 0, 0, 0.18)",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    borderRadius: "4px",
});

const titleStyleClass = StyleClassAuto({
    fontSize: "12px",
    lineHeight: "16px",
    color: "rgb(220, 220, 220)",
});

const activeVoiceStyleClass = StyleClassAuto({
    fontSize: "12px",
    lineHeight: "16px",
    color: "rgb(186, 186, 186)",
    margin: "2px 0 4px",
    whiteSpace: "normal",
});

const warningStyleClass = StyleClassAuto({
    fontSize: "11px",
    lineHeight: "15px",
    color: "rgb(205, 168, 112)",
    marginBottom: "5px",
});

const controlRowStyleClass = StyleClassAuto({
    display: "flex",
    alignItems: "center",
    gap: "6px",
});

const selectStyleClass = StyleClassAuto({
    flex: "0 1 260px",
    width: "260px",
    maxWidth: "100%",
    minWidth: "0",
    fontSize: "13px",
    lineHeight: "18px",
    color: "rgb(225, 225, 225)",
    background: "rgba(0, 0, 0, 0.35)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "3px",
    padding: "4px 6px",
    outline: "0",
});

const voiceArrowButtonStyleClass = StyleClassAuto({
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "24px",
    height: "28px",
    color: "rgb(225, 225, 225)",
    background: "rgba(0, 0, 0, 0.35)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "3px",
    fontSize: "20px",
    lineHeight: "20px",
});

const speakerButtonStyleClass = StyleClassAuto({
    flex: "0 0 auto",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: "28px",
    height: "28px",
    color: "rgb(225, 225, 225)",
    background: "rgba(0, 0, 0, 0.35)",
    border: "1px solid rgba(255, 255, 255, 0.16)",
    borderRadius: "3px",
});
